import { tenantControllerGetTenant } from '@/generated/korza-api-client';
import type { TenantDto } from '@/generated/korza-api-client/types.gen';
import { useQuery } from '@tanstack/react-query';
import { createContext, PropsWithChildren, useContext } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';

const TenantContext = createContext<TenantDto | 'loading' | null>(null);

export function useTenant(): TenantDto {
  const context = useContext(TenantContext);

  if (context === null || context === 'loading') {
    throw new Error();
  }

  return context;
}

export function useTenantNullable(): TenantDto | null {
  const context = useContext(TenantContext);
  if (context === 'loading') {
    return null;
  }
  return context;
}

const LOCAL_STORAGE_KEY = 'tenant';

export function tenantQueryKey(tenantSlug: string | undefined) {
  return ['tenant', tenantSlug] as const;
}

export async function fetchTenant(
  tenantSlug: string
): Promise<TenantDto | null> {
  const result = await tenantControllerGetTenant({
    path: { tenantSlug },
  });

  if (result.response.status === 404) {
    return null;
  }

  if (result.error || !result.data) {
    throw new Error('Failed to get tenant');
  }

  return result.data;
}

export function TenantProvider({ children }: PropsWithChildren) {
  const tenantSlug = useParams().tenantSlug;

  // const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  // const initialData: { slug: string; tenant: TenantDto | null } | undefined =
  //   stored !== null ? JSON.parse(stored) : undefined;

  const { data } = useQuery<{ slug: string; tenant: TenantDto | null }>({
    queryKey: tenantQueryKey(tenantSlug),
    queryFn: async () => {
      const tenant = await fetchTenant(tenantSlug ?? '');
      const result = { slug: tenantSlug ?? '', tenant };

      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(result));
      return result;
    },
    enabled: tenantSlug !== undefined,
    // initialData,
  });

  const value =
    data === undefined || data.slug !== tenantSlug ? 'loading' : data.tenant;

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

export function TenantGuard({ children }: PropsWithChildren) {
  const location = useLocation();
  const context = useContext(TenantContext);

  if (context === 'loading') {
    // Loading
    return <div></div>;
  }

  if (context === null) {
    const state = { redirect: { ...location, key: undefined } };

    console.log('Redirecting to /select-tenant', { state });

    return <Navigate to="/select-tenant" state={state} />;
  }

  return children;
}
